import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RadioButtonComponent, CheckBoxComponent, ButtonComponent } from "@syncfusion/ej2-react-buttons";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import "../Surveys/Surveys.css";
import "../../App.css";
import { getSurveyData, submitSurvey } from "../../services/SurveyApiService";
import "@syncfusion/ej2-base/styles/material.css";
import "@syncfusion/ej2-buttons/styles/material.css";
import type { Option, Question } from "../../types/survey";
import { getRevealedQuestionIds, shouldShowQuestion } from "../../utils/surveyUtils";

function SurveyPage() {
  const { userGuid, surveyGuid } = useParams();
  const navigate = useNavigate();
  const [surveyId, setSurveyId] = useState(Number);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [responses, setResponses] = useState<
    Record<string, { responseText: string; responseId: string; optionalComment?: string }>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [consumerName, setConsumerName] = useState("");
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [surveySubmitted, setSurveySubmitted] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState<string | null>(null);
  const [programProviderLogo, setProgramProviderLogo] = useState("");
  const [programProviderWebsite, setProgramProviderWebsite] = useState("");

  useEffect(() => {
    getSurveyData(userGuid, surveyGuid)
      .then((apiData) => {
        setSurveyId(apiData.surveyId);
        setConsumerName(apiData.consumerFirstName + " " + apiData.consumerLastName);
        setProgramProviderLogo(
          apiData.programProviderLogo ||
            "https://reliantpayment.com/wp-content/themes/reliant/assets/img/main-logo.svg"
        );
        setProgramProviderWebsite(apiData.programProviderWebsite || "https://reliantpayment.com/");
        setSurveyCompleted(apiData.surveyCompleted == true);
        const formattedQuestions = apiData.questions
          .sort((a: any, b: any) => a.displayOrder - b.displayOrder)
          .map((q: any) => ({
            id: q.questionId.toString(),
            type: q.questionType.toLowerCase() as "text" | "radio" | "radio-optional-text" | "checkbox-optional-text",
            questionText: q.questionText,
            options: q.options
              ?.sort((a: any, b: any) => a.displayOrder - b.displayOrder)
              .map((option: any) => ({
                id: option.optionId,
                text: option.optionText,
                revealQuestionId: option.revealQuestionId,
              })),
            required: q.isRequired,
          }));
        setQuestions(formattedQuestions);
        setLoading(false);
      })
      .catch(() => {
        setError("Survey not found or expired.");
        setLoading(false);
        navigate("/notfound");
      });
  }, [userGuid, surveyGuid]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;

  // Redirect to not found if surveyId is 11 --Colexit redirect surveys should not be accessible through this page
  if (surveyId === 11) {
    navigate("/notfound");
    return null;
  }

  const handleChange = (questionId: string, responseText: string, responseId: string) => {
    setResponses((prev) => ({ ...prev, [questionId]: { ...prev[questionId], responseText, responseId } }));
  };

  const handleCheckboxChange = (questionId: string, optionText: string, isChecked: boolean) => {
    setResponses((prev) => {
      const currentResponse = prev[questionId] || { responseText: "", responseId: "", optionalComment: "" };
      const selectedOptions = currentResponse.responseText
        ? currentResponse.responseText.split(", ")
        : [];

      if (isChecked) {
        selectedOptions.push(optionText);
      } else {
        const index = selectedOptions.indexOf(optionText);
        if (index > -1) selectedOptions.splice(index, 1);
      }

      return {
        ...prev,
        [questionId]: {
          ...currentResponse,
          responseText: selectedOptions.join(", "),
        },
      };
    });
  };

  const handleOptionalCommentChange = (questionId: string, optionalComment: string) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], optionalComment },
    }));
  };

  const handleSubmit = async () => {
    const unansweredRequiredQuestions = questions.filter(
      (q) => q.required && !responses[q.id]?.responseText
    );

    if (unansweredRequiredQuestions.length > 0) {
      alert("Please answer all required questions.");
      return;
    }

    const formattedResponses = Object.keys(responses).map((questionId) => {
      const question = questions.find((q) => q.id === questionId);
      const response = responses[questionId];

      // Combine option text and optional comment for radio-optional-text and checkbox-optional-text
      const responseText =
        question?.type === "radio-optional-text" || question?.type === "checkbox-optional-text"
          ? `${response.responseText}${response.optionalComment ? ` - ${response.optionalComment}` : ""}`
          : response.responseText;

      const formattedResponse: any = {
        surveyResponseId: 0,
        surveyActivityId: surveyGuid,
        surveyId: surveyId,
        surveyQuestionId: questionId,
        surveyQuestionText: question?.questionText,
        responseText: responseText, // Use the combined responseText
      };

      if (response.responseId) {
        formattedResponse.responseOptionId = response.responseId;
      }

      return formattedResponse;
    });

    try {
      const result = await submitSurvey(formattedResponses);
      if (result.isSuccess == true) {
        setSubmissionMessage(
          "Thank you for your valuable time and thoughtful responses. Your participation is greatly appreciated, and your feedback has been successfully submitted."
        );
      } else {
        setSubmissionMessage(
          "We apologize, but there was an issue submitting your responses. Please try again later."
        );
      }
    } catch (error) {
      setSubmissionMessage(
        "We apologize, but there was an issue submitting your responses. Please try again later."
      );
      navigate("/something-went-wrong");
    }
    setSurveySubmitted(true);
  };

  const getGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) {
      return "Good morning";
    } else if (currentHour < 18) {
      return "Good afternoon";
    } else {
      return "Good evening";
    }
  };

  return (
    <div className="container">
      <a href={programProviderWebsite} target="_blank" rel="noopener noreferrer">
        <span></span>
        <img src={programProviderLogo} alt="Logo" className="logo" />
      </a>
      <div className="welcome-message">
        <h3>
          {getGreeting()}, {consumerName}!
        </h3>
      </div>

      {surveyCompleted || surveySubmitted ? (
        surveySubmitted ? (
          <p>{submissionMessage}</p>
        ) : (
          <p>The survey has already been completed. Thank you for your participation.</p>
        )
      ) : (
        <>
          {questions.some((q) => q.required) && (
            <p className="">
              Questions marked with <span className="required-question-marker">*</span> are required.
            </p>
          )}
          {
            (() => {
              const revealedQuestionIds = getRevealedQuestionIds(questions);
              return questions.map((q) => {
                if (!shouldShowQuestion(q, revealedQuestionIds, responses, questions)) return null;
                return (
                  <div key={q.id} className="card">
                    <div className="card-header">
                      <h3>
                        {q.questionText} {q.required && <span className="required-question-marker">*</span>}
                      </h3>
                    </div>
                    <div className="card-content">
                      {q.type === "text" ? (
                        <TextBoxComponent
                          type="text"
                          placeholder="Your answer"
                          change={(e) => handleChange(q.id, (e as any).value, "")}
                        />
                      ) : q.type === "radio-optional-text" ? (
                        <>
                          {q.options?.map((option) => (
                            <div key={option.id} className="radio-option">
                              <RadioButtonComponent
                                label={option.text}
                                name={q.id}
                                change={() => handleChange(q.id, option.text, option.id)}
                              />
                            </div>
                          ))}
                          <TextBoxComponent
                            type="text"
                            placeholder="Optional comment"
                            change={(e) => handleOptionalCommentChange(q.id, (e as any).value)}
                          />
                        </>
                      ) : q.type === "checkbox-optional-text" ? (
                        <>
                          {q.options?.map((option) => (
                            <div key={option.id} className="checkbox-option">
                              <CheckBoxComponent
                                label={option.text}
                                change={(e) =>
                                  handleCheckboxChange(q.id, option.text, (e as any).checked)
                                }
                              />
                            </div>
                          ))}
                          <TextBoxComponent
                            type="text"
                            placeholder="Optional comment"
                            change={(e) => handleOptionalCommentChange(q.id, (e as any).value)}
                          />
                        </>
                      ) : q.type === "checkbox" ? (
                        <>
                          {q.options?.map((option) => (
                            <div key={option.id} className="checkbox-option">
                              <CheckBoxComponent
                                label={option.text}
                                change={(e) =>
                                  handleCheckboxChange(q.id, option.text, (e as any).checked)
                                }
                              />
                            </div>
                          ))}
                        </>
                      ) : (
                        q.options?.map((option) => (
                          <div key={option.id} className="radio-option">
                            <RadioButtonComponent
                              label={option.text}
                              name={q.id}
                              change={() => handleChange(q.id, option.text, option.id)}
                            />
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              });
            })()
          }
          <ButtonComponent className="e-primary submit-button" onClick={handleSubmit} disabled={surveySubmitted}>
            Submit
          </ButtonComponent>
        </>
      )}
    </div>
  );
}

export default SurveyPage;